import { EditType } from "@components/EditableCell"
import { Row } from "@components/VerticalTable"
import { GraphNodeData } from "@store/services/converter/types"

const coeffRegex = /(\b[0-9]+)(\.?)([0-9]+)?\b/g

export type ExpressionToken = {
  type: "text" | "coeff" | "node"
  value: string
}

type Replacement = {
  start: number
  end: number
  type: "coeff" | "node"
  value: string
}

export const getExpressionWithCoefficients = (
  nodeData: GraphNodeData,
  showAsVariables = true,
): ExpressionToken[] => {
  if (!nodeData.node?.properties.df_cpd.expression) return []

  const expression = nodeData.node.properties.df_cpd.expression[0]
  if (!expression) return []

  const replacements: Replacement[] = []

  // Collect coefficient positions
  const coeffArr: RegExpMatchArray[] = Array.from(expression.matchAll(coeffRegex))

  for (const [i, coeff] of coeffArr.entries()) {
    if (!coeff || typeof coeff.index !== "number") continue

    const value = coeff[0]
    const newValue = showAsVariables ? `{coeff${i + 1}: ${value}}` : value

    replacements.push({
      start: coeff.index,
      end: coeff.index + value.length,
      type: "coeff",
      value: newValue,
    })
  }

  /* 
  Replace parent node/attribute notation with the name in the expression
  Use word boundaries to match exact parentID, to skip some specific patterns,
  e.g. parentID = n2, expression = '(n1 == "n2")*2 + n22.loc[\'row\'][\'col\'] + arctan2(2, n2) + n2'
  where n22 is a table (attribute), "n2" is a categorical option, and arctan2 is a math function,
  should not be replaced
  */
  const getNodeReplacementRegex = (id: string) => {
    return new RegExp(`(?<!["'])\\b${id}\\b(?!["'])`, "g")
  }

  // Collect parent node positions
  nodeData.node.properties.parents_lst
    ?.slice()
    .sort()
    .reverse()
    .forEach((id) => {
      const regex = getNodeReplacementRegex(id)
      let match
      while ((match = regex.exec(expression)) !== null) {
        const label = nodeData.labelMap[id]
        if (label !== undefined) {
          replacements.push({
            start: match.index,
            end: match.index + id.length,
            type: "node",
            value: `{${label}}`,
          })
        }
      }
    })

  // Collect attribute positions
  nodeData.node.properties.attributes_lst
    ?.slice()
    .sort()
    .reverse()
    .forEach((id) => {
      const regex = getNodeReplacementRegex(id)
      let match
      while ((match = regex.exec(expression)) !== null) {
        const label = nodeData.labelMap[id]
        if (label !== undefined) {
          replacements.push({
            start: match.index,
            end: match.index + id.length,
            type: "node",
            value: `{${label}}`,
          })
        }
      }
    })

  // Sort replacements by start position
  replacements.sort((a, b) => a.start - b.start)

  // Build token array
  const tokens: ExpressionToken[] = []
  let lastPos = 0

  for (const replacement of replacements) {
    // Add text before this replacement
    if (replacement.start > lastPos) {
      tokens.push({
        type: "text",
        value: expression.slice(lastPos, replacement.start),
      })
    }
    // Add the replacement token
    tokens.push({
      type: replacement.type,
      value: replacement.value,
    })
    lastPos = replacement.end
  }

  // Add remaining text
  if (lastPos < expression.length) {
    tokens.push({
      type: "text",
      value: expression.slice(lastPos),
    })
  }

  return tokens
}

type CoefficientValue = {
  coefficient: string
  value: number
}
const getCoefficientValues = (expression: string): CoefficientValue[] => {
  if (!expression) {
    return []
  }

  const coeffArr: RegExpMatchArray[] = Array.from(expression.matchAll(coeffRegex))
  const values: CoefficientValue[] = []

  for (const [i, coeff] of coeffArr.entries()) {
    const value = coeff[0]

    if (typeof coeff.index === "number") {
      values.push({
        coefficient: `coeff${i + 1}`,
        value: Number(value),
      })
    }
  }

  return values
}

type RowOptions = Omit<Row, "onBlur"> & {
  onBlur: (coefficient: string, newValue: number) => void // override, we want to additionaly pass info which coefficient
}

export const getCoefficientRows = (expression: string, options: Partial<RowOptions>): Row[] => {
  if (!expression) {
    return []
  }

  return getCoefficientValues(expression).map((coeff) => ({
    label: coeff.coefficient,
    value: coeff.value,
    type: EditType.Number,
    ...options,
    onBlur: (newValue) => options.onBlur?.(coeff.coefficient, Number(newValue)),
  }))
}

export const getUpdatedExpression = (expression: string, coefficient: string, newValue: number) => {
  const values = getCoefficientValues(expression)

  const valueIndex = values.findIndex((value) => value.coefficient === coefficient)
  if (valueIndex === -1) return expression

  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  values[valueIndex] = { ...values[valueIndex], value: newValue }

  return replaceExpressionCoefficients(expression, values)
}

export const replaceExpressionCoefficients = (
  expression: string,
  coefficients: CoefficientValue[],
): string => {
  if (!expression) {
    return ""
  }

  const coeffArr: RegExpMatchArray[] = Array.from(expression.matchAll(coeffRegex))
  let updatedExpression = expression
  let characterIndex = 0

  for (const [i, coeff] of coeffArr.entries()) {
    const value = coeff[0]
    const coefficient = coefficients.find((coef) => coef.coefficient === `coeff${i + 1}`)

    if (typeof coeff.index === "number" && coefficient) {
      updatedExpression =
        updatedExpression.substring(0, coeff.index + characterIndex) +
        coefficient.value +
        updatedExpression.substring(coeff.index + value.length + characterIndex)
      characterIndex += coefficient.value.toString().length - value.length
    }
  }

  return updatedExpression
}
